import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "contexts/ToastContext";
import { getInitials, formatDate } from "utils/helpers";
import { LoadingIcon } from "utils/constant";
import { CountUp } from "pages/dashboard/DashboardCharts";
import {
    sectorEvaluationApi,
    DirectiveEvaluationApi,
} from "services/directiveEvaluationService";

interface TeamScore {
    speed: string;
    quality: string;
    load: string;
}

interface ExistingLink {
    id?: string;
    userId: string;
    name?: string;
}

interface ExistingEvaluation {
    evaluationId: string;
    speed: string;
    quality: string;
    load: string;
    evaluatedBy?: string;
    evaluationDate?: string;
    /** Overall score worked out by the server, when it sends one. */
    overall?: number | null;
    links: ExistingLink[];
}

interface EvaluationModalProps {
    /** The directive (accuracy) request id the evaluation belongs to */
    requestId: string;
    /** prosecutor_assignments list ({ prosecutor_user_id, prosecutor_name, is_team_leader }) */
    teamMembers: any[];
    /** Display name stored on the evaluation as evaluated_by */
    evaluatedBy?: string;
    /** Bearer token */
    bearer: string;
    /** Which module's evaluation endpoints to use (defaults to sector/accuracy). */
    api?: DirectiveEvaluationApi;
    onClose: () => void;
    onSuccess?: () => void;
}

const FIELDS: { key: keyof TeamScore; label: string; fallback: string }[] = [
    { key: "speed", label: "WorkSpeed", fallback: "Work Speed" },
    { key: "quality", label: "WorkQuality", fallback: "Work Quality" },
    { key: "load", label: "WorkLoad", fallback: "Work Load" },
];

const emptyScore = (): TeamScore => ({ speed: "", quality: "", load: "" });

// Overall score ring in the dashboards' style; the arc and number build up.
const ScoreRing = ({ value, label }: { value: number | null; label: string }) => {
    const radius = 42;
    const circumference = 2 * Math.PI * radius;
    const [shown, setShown] = useState(0);
    useEffect(() => {
        const target = value ?? 0;
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            setShown(target);
            return;
        }
        const start = performance.now();
        let frame = 0;
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / 900);
            setShown(target * (1 - Math.pow(1 - t, 3)));
            if (t < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [value]);
    const length = (shown / 100) * circumference;
    return (
        <div className="flex items-center gap-5">
            <svg viewBox="0 0 100 100" className="h-24 w-24 shrink-0">
                <circle cx="50" cy="50" r={radius} fill="none" stroke="#F2F2F2" strokeWidth="10" />
                <circle
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke="#AA232F"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${length} ${circumference - length}`}
                    transform="rotate(-90 50 50)"
                />
                <text x="50" y="56" textAnchor="middle" className="fill-gray-900 text-[20px] font-bold">
                    {value === null ? "—" : `${Math.round(shown)}%`}
                </text>
            </svg>
            <div>
                <p className="font-semibold text-gray-900">{label}</p>
            </div>
        </div>
    );
};

const asArray = (res: any): any[] =>
    Array.isArray(res) ? res : res?.results ?? [];

// "85.00" -> "85", "" / null -> ""
const normNum = (v: any): string => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    return isNaN(n) ? "" : String(n);
};

export default function EvaluationModal({
    requestId,
    teamMembers,
    evaluatedBy,
    bearer,
    api = sectorEvaluationApi,
    onClose,
    onSuccess,
}: EvaluationModalProps) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [loadingExisting, setLoadingExisting] = useState(true);
    const [mode, setMode] = useState<"view" | "edit">("edit");
    const [existing, setExisting] = useState<ExistingEvaluation | null>(null);
    const [teamScore, setTeamScore] = useState<TeamScore>(emptyScore());

    const members = useMemo(
        () =>
            (teamMembers ?? []).map((m: any) => ({
                userId: String(m.prosecutor_user_id ?? m.user_id ?? m.id ?? ""),
                name:
                    m.prosecutor_name ||
                    m.name ||
                    m.user_name ||
                    m.full_name ||
                    t("Prosecutor") ||
                    "Prosecutor",
                isLeader: m.is_team_leader ?? m.isTeamLeader ?? m.role === "leader",
            })),
        [teamMembers, t]
    );

    const buildExisting = (
        evalRes: any,
        prosList: any[]
    ): ExistingEvaluation | null => {
        const evaluations = asArray(evalRes).filter(
            (e: any) => String(e.directive_request) === String(requestId)
        );
        if (evaluations.length === 0) return null;
        // One team evaluation per request — take the most recent.
        const e =
            [...evaluations].sort((a, b) =>
                String(b.evaluation_date ?? b.cretedAt ?? "").localeCompare(
                    String(a.evaluation_date ?? a.cretedAt ?? "")
                )
            )[0] || evaluations[0];
        const rawLinks =
            e.prosecutor_evaluations && e.prosecutor_evaluations.length
                ? e.prosecutor_evaluations
                : prosList.filter(
                      (p: any) => String(p.evaluation) === String(e.id)
                  );
        return {
            evaluationId: String(e.id),
            speed: normNum(e.work_speed_evaluation),
            quality: normNum(e.work_quality_evaluation),
            load: normNum(e.work_load_evaluation),
            evaluatedBy: e.evaluated_by,
            evaluationDate: e.evaluation_date,
            overall:
                e.overall_score === undefined || e.overall_score === null
                    ? null
                    : Number(e.overall_score),
            links: rawLinks.map((p: any) => ({
                id: p.id ? String(p.id) : undefined,
                userId: String(p.prosecutor_user_id ?? ""),
                name: p.prosecutor_name,
            })),
        };
    };

    const loadExisting = async () => {
        const evalRes = await api.getEvaluations(bearer, requestId);
        const built = buildExisting(evalRes, []);
        // Only when the evaluation came without its member links, fetch the
        // links of that one evaluation (not every link in the system).
        if (built && built.links.length === 0) {
            try {
                const prosList = asArray(
                    await api.getProsecutors(bearer, built.evaluationId)
                );
                return buildExisting(evalRes, prosList);
            } catch {
                return built;
            }
        }
        return built;
    };

    // Load the existing team evaluation (if any) on open.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const ev = await loadExisting();
                if (!active) return;
                setExisting(ev);
                if (ev) {
                    setTeamScore({
                        speed: ev.speed,
                        quality: ev.quality,
                        load: ev.load,
                    });
                    setMode("view");
                } else {
                    setMode("edit");
                }
            } catch (err) {
                console.error("Failed to load evaluation:", err);
            } finally {
                if (active) setLoadingExisting(false);
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bearer, requestId]);

    const setField = (key: keyof TeamScore, value: string) => {
        let v = value;
        if (v !== "") {
            const n = Number(v);
            if (isNaN(n)) return;
            if (n < 0) v = "0";
            if (n > 100) v = "100";
        }
        setTeamScore((prev) => ({ ...prev, [key]: v }));
    };

    const average = (s: TeamScore) => {
        const vals = [s.speed, s.quality, s.load]
            .filter((x) => x !== "")
            .map(Number);
        if (vals.length === 0) return null;
        return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    };

    const isComplete = (s: TeamScore) =>
        s.speed !== "" && s.quality !== "" && s.load !== "";

    const handleSubmit = async () => {
        if (!isComplete(teamScore)) {
            showToast(
                t("EnterAllThreeScores") ||
                    "Enter all three team scores (0-100).",
                "error"
            );
            return;
        }
        if (members.length === 0) {
            showToast(
                t("NoTeamMembers") || "No team members to evaluate.",
                "error"
            );
            return;
        }
        setLoading(true);
        try {
            const payload = {
                directive_request: requestId,
                evaluated_by: evaluatedBy,
                evaluation_date: new Date().toISOString(),
                work_speed_evaluation: Number(teamScore.speed),
                work_quality_evaluation: Number(teamScore.quality),
                work_load_evaluation: Number(teamScore.load),
            };

            let evaluationId: string;
            if (existing?.evaluationId) {
                await api.updateEvaluation(
                    bearer,
                    existing.evaluationId,
                    payload
                );
                evaluationId = existing.evaluationId;
            } else {
                const created = await api.createEvaluation(bearer, payload);
                evaluationId = created.id;
            }

            // Some services link the team on the server; create only the
            // links still missing, in parallel.
            let refreshed = await loadExisting();
            const linkedIds = new Set((refreshed?.links ?? []).map((l) => l.userId));
            const missing = members.filter((m) => !linkedIds.has(m.userId));
            if (missing.length) {
                await Promise.all(
                    missing.map((m) =>
                        api.createProsecutor(bearer, {
                            evaluation: evaluationId,
                            prosecutor_user_id: m.userId,
                            prosecutor_name: m.name,
                        })
                    )
                );
                refreshed = await loadExisting();
            }

            showToast(
                t("EvaluationSubmitted") || "Evaluation submitted successfully.",
                "success"
            );
            setExisting(refreshed);
            setMode("view");
            onSuccess?.();
        } catch (err) {
            console.error("Evaluation submit failed:", err);
            showToast(
                t("FailedToSubmitEvaluation") || "Failed to submit evaluation.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const hasExisting = !!existing;
    // The server's overall score while viewing, the live average while editing.
    const teamAvg =
        mode === "view" && existing?.overall !== null && existing?.overall !== undefined
            ? existing.overall
            : average(teamScore);

    // Team roster — shown in both modes.
    const Roster = (
        <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {t("TeamMembers") || "Team Members"} ({members.length})
            </p>
            {members.length === 0 ? (
                <p className="text-sm text-gray-400 italic">
                    {t("NoTeamMembers") || "No team members."}
                </p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {members.map((m) => (
                        <div
                            key={m.userId}
                            className="flex items-center gap-2 bg-[#FDEBEC] border border-[#AA232F]/20 rounded-full pl-1 pr-3 py-1"
                        >
                            <div className="w-7 h-7 rounded-full bg-[#AA232F] text-white flex items-center justify-center font-semibold text-[11px] select-none">
                                {getInitials(m.name)}
                            </div>
                            <span className="text-sm text-gray-700">{m.name}</span>
                            {m.isLeader && (
                                <span className="text-[9px] font-medium text-[#AA232F] border border-[#AA232F] rounded-full px-1.5 py-0.5">
                                    {t("Leader") || "Leader"}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-[#AA232F] px-6 py-5 flex items-start justify-between">
                    <div>
                        <p className="text-red-100 text-xs font-semibold uppercase tracking-wider">
                            {t("OfficeOfTheAttorneyGeneral") ||
                                "Office of the Attorney General"}
                        </p>
                        <h2 className="text-white text-xl font-bold mt-1">
                            {t("TeamPerformanceEvaluation") ||
                                "Team Performance Evaluation"}
                        </h2>
                        <p className="text-red-100 text-sm mt-1">
                            {mode === "view"
                                ? t("TeamEvaluationResult") ||
                                  "This evaluation applies to the whole team."
                                : t("RateTheTeam") ||
                                  "Rate the team from 0 to 100% — it applies to all members."}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition"
                        aria-label="Close"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5 overflow-y-auto scrollbar-hide">
                    {loadingExisting ? (
                        <div className="flex items-center justify-center py-12 text-gray-500 gap-2">
                            {LoadingIcon} {t("Loading") || "Loading"}
                        </div>
                    ) : (
                        <>
                            {/* Roster */}
                            <div className="border border-gray-200 rounded-xl p-4 shadow-sm">
                                {Roster}
                            </div>

                            {/* Team average summary */}
                            <div className="border border-gray-200 rounded-xl px-5 py-4 shadow-sm">
                                <ScoreRing
                                    value={teamAvg}
                                    label={t("OverallTeamScore") || "Overall team score"}
                                />
                                <p className="mt-2 text-xs text-gray-500">
                                    {t("AverageOfThreeMetrics") ||
                                        "Average of the three metrics"}
                                </p>
                            </div>

                            {/* Scores */}
                            {mode === "view" ? (
                                <div className="space-y-3">
                                    {FIELDS.map((f) => {
                                        const val = Number(teamScore[f.key] || 0);
                                        return (
                                            <div key={f.key}>
                                                <div className="flex justify-between text-sm text-gray-600 mb-1">
                                                    <span>
                                                        {t(f.label) || f.fallback}
                                                    </span>
                                                    <span className="font-semibold text-gray-900">
                                                        {teamScore[f.key] === "" ? (
                                                            "—"
                                                        ) : (
                                                            <>
                                                                <CountUp value={val} />%
                                                            </>
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="w-full h-2 bg-[#F2F2F2] rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full bg-[#AA232F] transition-[width] duration-700 ease-out"
                                                        style={{
                                                            width: `${val}%`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {(existing?.evaluatedBy ||
                                        existing?.evaluationDate) && (
                                        <p className="text-[11px] text-gray-400 pt-1">
                                            {existing?.evaluatedBy &&
                                                `${t("EvaluatedBy") || "Evaluated by"}: ${existing.evaluatedBy}`}
                                            {existing?.evaluatedBy &&
                                                existing?.evaluationDate &&
                                                " · "}
                                            {existing?.evaluationDate &&
                                                formatDate(
                                                    existing.evaluationDate
                                                )}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {FIELDS.map((f) => (
                                        <div key={f.key}>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-sm font-medium text-gray-700">
                                                    {t(f.label) || f.fallback}
                                                </label>
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        value={teamScore[f.key]}
                                                        onChange={(e) =>
                                                            setField(
                                                                f.key,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="0-100"
                                                        className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#AA232F]/30 focus:border-[#AA232F]"
                                                    />
                                                    <span className="text-sm text-gray-400">
                                                        %
                                                    </span>
                                                </div>
                                            </div>
                                            <input
                                                type="range"
                                                min={0}
                                                max={100}
                                                value={
                                                    teamScore[f.key] === ""
                                                        ? 0
                                                        : Number(teamScore[f.key])
                                                }
                                                onChange={(e) =>
                                                    setField(
                                                        f.key,
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full accent-[#AA232F] cursor-pointer"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 bg-gray-50">
                    {mode === "view" ? (
                        <>
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition"
                            >
                                {t("Close") || "Close"}
                            </button>
                            <button
                                onClick={() => setMode("edit")}
                                className="px-6 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold transition flex items-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t("EditEvaluation") || "Edit Evaluation"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={
                                    hasExisting ? () => setMode("view") : onClose
                                }
                                disabled={loading}
                                className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition disabled:opacity-50"
                            >
                                {t("Cancel") || "Cancel"}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={loading || members.length === 0}
                                className="px-6 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold transition disabled:opacity-50 flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        {LoadingIcon}{" "}
                                        {t("Submitting") || "Submitting"}
                                    </>
                                ) : hasExisting ? (
                                    t("SaveChanges") || "Save Changes"
                                ) : (
                                    t("SubmitEvaluation") || "Submit Evaluation"
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
